import { Injectable } from '@angular/core';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map, pluck } from 'rxjs/operators';

import { filterDefined } from 'utils/is-defined';

import { FormService } from './form.service';

import { ISPFieldConfig, ISPFieldType } from '../model';
import { filterConfigs } from '../utils/formly-configs';

@UntilDestroy()
@Injectable()
export class FontService {
  private readonly fontFamilyControllersSubject: BehaviorSubject<
    Record<string, string>
  > = new BehaviorSubject({});

  private readonly fontSizeControllersSubject: BehaviorSubject<
    Record<string, string>
  > = new BehaviorSubject({});

  constructor(private readonly formService: FormService) {
    this.subscribeToFontFamilyControllers();
    this.subscribeToFontSizeControllers();
  }

  private subscribeToFontFamilyControllers(): void {
    this.formService.configs$
      .pipe(
        map(configs => {
          return filterConfigs(configs, config =>
            Boolean(
              config.type === ISPFieldType.Select &&
                config.props.originalControl.$targetfontfamilyview,
            ),
          ).reduce(
            (controllers, config: ISPFieldConfig<ISPFieldType.Select>) => {
              controllers[config.props.originalControl.$targetfontfamilyview!] =
                config.props.originalControl.$name;
              return controllers;
            },
            {},
          );
        }),
        untilDestroyed(this),
      )
      .subscribe(this.fontFamilyControllersSubject);
  }

  private subscribeToFontSizeControllers(): void {
    this.formService.configs$
      .pipe(
        map(configs => {
          return filterConfigs(configs, config =>
            Boolean(
              config.type === ISPFieldType.Slider &&
                config.props.originalControl.$targetfontsizeview,
            ),
          ).reduce(
            (controllers, config: ISPFieldConfig<ISPFieldType.Slider>) => {
              controllers[config.props.originalControl.$targetfontsizeview!] =
                config.props.originalControl.$name;
              return controllers;
            },
            {},
          );
        }),
        untilDestroyed(this),
      )
      .subscribe(this.fontSizeControllersSubject);
  }

  getFontSizeForControl$(name: string): Observable<string> {
    return combineLatest([
      this.formService.valueChanges$,
      this.fontSizeControllersSubject.pipe(pluck(name)),
    ]).pipe(
      map(([model, controllerName]) => {
        if (controllerName) {
          const fontSizeValue = model[controllerName] as string;
          return fontSizeValue;
        }
      }),
      filterDefined(),
    );
  }

  getFontFamilyForControl$(name: string): Observable<string> {
    return combineLatest([
      this.formService.valueChanges$,
      this.fontFamilyControllersSubject.pipe(pluck(name)),
    ]).pipe(
      map(([model, controllerName]) => {
        if (controllerName) {
          const fontFamilyValue = model[controllerName] as string;
          return fontFamilyValue;
        }
      }),
      filterDefined(),
    );
  }
}
